import React from 'react';
import { 
  Shield, 
  Info, 
  DollarSign, 
  ShoppingCart, 
  Copyright, 
  Scale, 
  Gavel,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <button 
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-[#1e3a5f] transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <Scale className="text-[#1e3a5f]" size={48} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">Terms and Conditions</h1>
          <p className="text-gray-500 text-sm">Last updated: {new Date().toLocaleDateString()}</p>
          <div className="w-20 h-1 bg-[#1e3a5f] mx-auto mt-4 rounded-full"></div>
        </div>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-100">
          <p className="text-gray-700 leading-relaxed">
            Welcome to DP Jewellers. By accessing our website and using our services, you agree to be bound by the following terms and conditions. 
            Please read them carefully before making a purchase.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <Shield className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">1. General Provisions</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            These Terms and Conditions govern your use of the DP Jewellers website and the purchase of products from us. 
            By using our website, you confirm that you are at least 18 years old or accessing the site under the supervision of a parent or guardian.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <Info className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">2. Product Information and Accuracy</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            We strive to display our products as accurately as possible. However, due to the intricate nature of jewellery 
            and differences in monitor displays, actual colors and textures may vary slightly. All product descriptions, 
            weights, and dimensions are approximate and provided for convenience.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <DollarSign className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">3. Pricing and Gold Rates</h2>
          </div>
          <p className="text-gray-700 leading-relaxed mb-3">
            Prices for our products are subject to change without notice. The price of gold and silver fluctuates daily based on market rates. 
            The final price payable will be confirmed at the time of checkout.
          </p>
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <p className="text-amber-800 text-sm flex items-center gap-2">
              <span className="text-lg">💎</span>
              We reserve the right to modify or discontinue any product at any time.
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <ShoppingCart className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">4. Orders and Acceptance</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            Your receipt of an electronic or other form of order confirmation does not signify our acceptance of your order. 
            DP Jewellers reserves the right at any time after receipt of your order to accept or decline your order for any reason.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <Copyright className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">5. Intellectual Property</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            All content on this website, including designs, text, graphics, logos, images, and software, is the property of 
            DP Jewellers and is protected by Indian and international copyright laws. Unauthorized use of any material is strictly prohibited.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <Gavel className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">6. Limitation of Liability</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            DP Jewellers shall not be liable for any special or consequential damages that result from the use of, or the inability to use, 
            the materials on this site or the performance of the products, even if DP Jewellers has been advised of the possibility of such damages.
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6 hover:shadow-xl transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-[#1e3a5f]/10 p-2 rounded-lg">
              <Scale className="text-[#1e3a5f]" size={24} />
            </div>
            <h2 className="text-xl font-bold text-gray-800">7. Governing Law</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            These Terms and Conditions shall be governed by and construed in accordance with the laws of India. 
            Any disputes arising out of or in connection with these terms shall be subject to the exclusive jurisdiction 
            of the courts in Ballia, Uttar Pradesh.
          </p>
        </div>
        <div className="bg-blue-50 rounded-xl p-6 mb-6 border border-blue-100">
          <h3 className="font-semibold text-[#1e3a5f] mb-3 flex items-center gap-2">
            <CheckCircle size={18} />
            Need Clarification?
          </h3>
          <p className="text-gray-700 text-sm mb-3">
            If you have any questions about our Terms and Conditions, please contact us:
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a 
              href="tel:+918953786652"
              className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#153057] text-sm"
            >
              📞 +91 89537 86652
            </a>
            <a 
              href="mailto:info@dpjewellersballia.in"
              className="inline-flex items-center gap-2 text-[#1e3a5f] hover:text-[#153057] text-sm"
            >
              ✉️ info@dpjewellersballia.in
            </a>
          </div>
        </div>
        <div className="text-center text-gray-400 text-xs mt-8">
          <p>DP Jewellers - Trusted Since 1941</p>
          <p className="mt-1">© {new Date().getFullYear()} DP Jewellers. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;